import { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { X } from 'lucide-react';

/**
 * Cascading notion picker: Matière → Thème → Section → Notion
 * Compact, mobile-friendly. Takes the full tags tree and selected notion_id.
 */
export function NotionPicker({ tagsTree, value, onChange, testIdPrefix }) {
  const pfx = testIdPrefix || 'np';

  // Build flat lookup map
  const byId = useMemo(() => {
    const map = {};
    const walk = (nodes) => {
      for (const n of (nodes || [])) {
        map[n.tag_id] = n;
        walk(n.children);
      }
    };
    walk(tagsTree);
    return map;
  }, [tagsTree]);

  // Resolve current selection path from notion_id
  const resolvedPath = useMemo(() => {
    if (!value || !byId[value]) return {};
    const notion = byId[value];
    let chapter = byId[notion.parent_id];
    let theme = chapter ? byId[chapter.parent_id] : null;
    let subject = theme ? byId[theme.parent_id] : null;
    return {
      subject_id: subject?.tag_id || '',
      theme_id: theme?.tag_id || '',
      chapter_id: chapter?.tag_id || '',
      notion_id: value,
    };
  }, [value, byId]);

  const [subjectId, setSubjectId] = useState(resolvedPath.subject_id || '');
  const [themeId, setThemeId] = useState(resolvedPath.theme_id || '');
  const [chapterId, setChapterId] = useState(resolvedPath.chapter_id || '');

  // Sync when value changes externally (e.g., auto-suggest)
  useEffect(() => {
    setSubjectId(resolvedPath.subject_id || '');
    setThemeId(resolvedPath.theme_id || '');
    setChapterId(resolvedPath.chapter_id || '');
  }, [resolvedPath.subject_id, resolvedPath.theme_id, resolvedPath.chapter_id]);

  const subjects = useMemo(() => tagsTree.filter(t => t.type === 'subject'), [tagsTree]);
  const themes = useMemo(() => subjectId ? (byId[subjectId]?.children || []).filter(t => t.type === 'theme') : [], [subjectId, byId]);
  const chapters = useMemo(() => themeId ? (byId[themeId]?.children || []).filter(t => t.type === 'chapter') : [], [themeId, byId]);
  const notions = useMemo(() => chapterId ? (byId[chapterId]?.children || []).filter(t => t.type === 'notion') : [], [chapterId, byId]);

  const selectedNotion = value ? byId[value] : null;

  const handleClear = () => {
    setSubjectId('');
    setThemeId('');
    setChapterId('');
    onChange('');
  };

  // If there's only 1 option at any level, auto-select it
  useEffect(() => {
    if (subjects.length === 1 && !subjectId) setSubjectId(subjects[0].tag_id);
  }, [subjects, subjectId]);

  useEffect(() => {
    if (themes.length === 1 && !themeId) setThemeId(themes[0].tag_id);
  }, [themes, themeId]);

  useEffect(() => {
    if (chapters.length === 1 && !chapterId) setChapterId(chapters[0].tag_id);
  }, [chapters, chapterId]);

  // Show compact label if notion is selected
  if (selectedNotion) {
    const chapter = byId[selectedNotion.parent_id];
    const theme = chapter ? byId[chapter.parent_id] : null;
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${pfx}-selected`}>
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 font-medium truncate max-w-[200px]">
          {selectedNotion.label}
        </span>
        {theme && <span className="text-xs text-muted-foreground hidden sm:inline truncate max-w-[150px]">{theme.label} › {chapter?.label}</span>}
        <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" onClick={handleClear} data-testid={`${pfx}-clear`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  // Cascading selectors — show step by step
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${pfx}-cascade`}>
      {/* Step 1: Subject */}
      {subjects.length > 1 && (
        <Select value={subjectId || '_pick'} onValueChange={(v) => { setSubjectId(v === '_pick' ? '' : v); setThemeId(''); setChapterId(''); onChange(''); }}>
          <SelectTrigger className="h-7 w-28 sm:w-32 text-xs" data-testid={`${pfx}-subject`}>
            <SelectValue placeholder="Matière" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_pick"><span className="text-muted-foreground">Matière...</span></SelectItem>
            {subjects.map(s => <SelectItem key={s.tag_id} value={s.tag_id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {/* Step 2: Theme */}
      {subjectId && themes.length > 1 && (
        <Select value={themeId || '_pick'} onValueChange={(v) => { setThemeId(v === '_pick' ? '' : v); setChapterId(''); onChange(''); }}>
          <SelectTrigger className="h-7 w-28 sm:w-32 text-xs" data-testid={`${pfx}-theme`}>
            <SelectValue placeholder="Thème" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_pick"><span className="text-muted-foreground">Thème...</span></SelectItem>
            {themes.map(t => <SelectItem key={t.tag_id} value={t.tag_id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {/* Step 3: Section */}
      {themeId && chapters.length > 1 && (
        <Select value={chapterId || '_pick'} onValueChange={(v) => { setChapterId(v === '_pick' ? '' : v); onChange(''); }}>
          <SelectTrigger className="h-7 w-28 sm:w-36 text-xs" data-testid={`${pfx}-section`}>
            <SelectValue placeholder="Section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_pick"><span className="text-muted-foreground">Section...</span></SelectItem>
            {chapters.map(c => <SelectItem key={c.tag_id} value={c.tag_id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {/* Step 4: Notion */}
      {chapterId && notions.length > 0 && (
        <Select value="_pick" onValueChange={(v) => { if (v !== '_pick') onChange(v); }}>
          <SelectTrigger className="h-7 w-32 sm:w-40 text-xs border-primary/30" data-testid={`${pfx}-notion`}>
            <SelectValue placeholder="Notion..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_pick"><span className="text-muted-foreground">Notion...</span></SelectItem>
            {notions.map(n => <SelectItem key={n.tag_id} value={n.tag_id}>{n.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {/* Hint if no subject selected */}
      {!subjectId && subjects.length <= 1 && subjects.length === 0 && (
        <span className="text-xs text-muted-foreground">Aucune notion disponible</span>
      )}
    </div>
  );
}
